import {
  membershipService,
  queryClientOptions,
  shiftService,
  weeklyScheduleService
} from '@libs/data-access/queries'
import { defineModal, Modal } from '@libs/ui/ds'
import { useTrackScheduleFeaturesInteraction } from '@spa/core/externals/segment'
import type { ScheduleFeatureShiftPlanificationType } from '@spa/core/externals/segment/methods/track/types'
import { QueriesRequestsStatus } from '@spa/features/Queries'
import { realHoursModalDef } from '@spa/scenes/Plannings/Modals/RealHoursModal/RealHoursModal'
import type { FC } from 'react'
import React from 'react'
import { getRoleRights } from 'snap-redux/modules/memberships/utils'

import { UnvalidateShiftForm } from '../../Forms'

type UnvalidateHoursModalProps = {
  shiftId: number
  membershipId: number
  weeklyscheduleId: number
  // For Month View V2, we compute the possibility to invalidate a shift directly via the permissions on a shift
  precomputedCanValidate?: boolean
  precomputedCanInvalidate?: boolean
  onSuccess?: () => void
}

export const unvalidateHoursModalDef = defineModal<UnvalidateHoursModalProps>({
  name: 'unvalidateHoursModal'
})

const UnvalidateHoursModal: FC = () => {
  const scheduleTracker = useTrackScheduleFeaturesInteraction()
  const { close, isOpen, data } = unvalidateHoursModalDef.useModal()
  const { open: openRealHours } = realHoursModalDef.useDispatchModal()

  const {
    shiftId,
    membershipId,
    weeklyscheduleId,
    precomputedCanValidate,
    precomputedCanInvalidate,
    onSuccess
  } = data as UnvalidateHoursModalProps
  const queryMembership = membershipService.membership.useFetchCurrent()
  const queryShift = shiftService.api.useFetchShift({
    pathParams: { id: shiftId }
  })

  const roles = getRoleRights(queryMembership.data)

  // Allow us to prefetch data for the membership of the shift
  membershipService.membership.useFetchOne(
    {
      pathParams: { membership_id: membershipId }
    },
    { staleTime: queryClientOptions.time.STATIC }
  )

  const queryWeeklyScheduleShift = weeklyScheduleService.api.useFetchOne(
    {
      pathParams: { id: weeklyscheduleId },
      queryParams: { with_permissions: true }
    },
    { staleTime: queryClientOptions.time.STATIC }
  )

  const computeInvalidationPermission = () => {
    // Month View V2
    if (precomputedCanInvalidate !== undefined) {
      return precomputedCanInvalidate
    }
    // Other views
    return queryWeeklyScheduleShift.data?.permissions?.can_validate
  }

  const canInvalidateShift = computeInvalidationPermission()

  const isFetching =
    !queryMembership.data || !queryShift.data || !queryWeeklyScheduleShift.data

  if (isFetching) {
    return (
      <QueriesRequestsStatus
        statuses={[
          queryMembership.status,
          queryShift.status,
          queryWeeklyScheduleShift.status
        ]}
        errors={[
          queryMembership.error,
          queryShift.error,
          queryWeeklyScheduleShift.error
        ]}
      />
    )
  }

  return (
    <Modal opened={isOpen} onClose={close} variant='right'>
      <UnvalidateShiftForm
        shift={queryShift.data}
        isManager={roles.atLeast.manager}
        canInvalidate={canInvalidateShift ?? false}
        formSuccess={() => {
          close()
          onSuccess?.()
          scheduleTracker.scheduleElementCardOpened(
            'shift',
            queryShift.data
              ?.planification_type as ScheduleFeatureShiftPlanificationType,
            {
              published: queryWeeklyScheduleShift.data.published,
              validated: Boolean(queryShift.data?.validated_at),
              declared: Boolean(queryShift.data?.real_hours_declared_correct_at)
            }
          )
          openRealHours({
            shiftId,
            weeklyscheduleId,
            membershipId,
            precomputedCanValidate,
            onSuccess
          })
        }}
      />
    </Modal>
  )
}

export default unvalidateHoursModalDef.withModalShow(UnvalidateHoursModal)
